
import type { ReactNode } from 'react';
import { useI18n } from '../../i18n';
import { EmptyState, Icon, SectionNav, Spinner } from '../../ui';
import { Link } from 'react-router';
import { useCurrentCompany, useTool } from '../companies/hooks';

export const settingsPath = (tenantId: string, section: string) => `/c/${tenantId}/settings/${section}`;

/** Members and Teams for everybody in the company; the rest for its owners and admins. */
export function SettingsTabs({ tenantId }: { tenantId: string }) {
  const { t } = useI18n();
  const { isAdmin } = useCurrentCompany();
  // Automation is a tool of the Marketplace (TF-171): its tab only while it is switched on.
  const automationOn = useTool('automation');
  const tabs = [
    { section: 'members', label: t('nav.members') },
    { section: 'teams', label: t('settings.teams') },
    ...(isAdmin
      ? [
          { section: 'company', label: t('settings.company') },
          { section: 'fields', label: t('settings.fields') },
          { section: 'tags', label: t('settings.tags') },
          { section: 'deal-lines', label: t('settings.dealLines') },
          { section: 'relations', label: t('settings.relationTypes') },
          { section: 'document-types', label: t('settings.documentTypes') },
          { section: 'files', label: t('settings.files') },
          { section: 'communication', label: t('settings.communication') },
          { section: 'mass-mail', label: t('settings.massMail') },
          { section: 'integrations', label: t('settings.marketplace') },
          { section: 'assistants', label: t('settings.assistants') },
          ...(automationOn ? [{ section: 'automation', label: t('settings.automation') }] : []),
          { section: 'forms', label: t('settings.forms') },
          { section: 'audit', label: t('settings.audit') },
          { section: 'billing', label: t('settings.billing') },
          { section: 'privacy', label: t('settings.privacy') },
        ]
      : []),
  ];
  // Twelve sections for an owner: what does not fit goes under "More settings" (TF-215).
  return <SectionNav label={t('nav.settings')} moreLabel={t('nav.moreSettings')} links={tabs.map((tab) => ({ to: settingsPath(tenantId, tab.section), label: tab.label }))} />;
}

/**
 * The page of a Marketplace tool (TF-171): switched off for the company, it says so and where to switch
 * it on, instead of a page whose every request would be refused.
 */
export function ToolOnly({ tenantId, tool, message, children }: { tenantId: string; tool: string; message: string; children: ReactNode }) {
  const { t } = useI18n();
  if (useTool(tool)) return <>{children}</>;
  return (
    <EmptyState icon={<Icon name="extension" size={40} />} title={message}>
      <Link className="btn btn-tonal" to={settingsPath(tenantId, 'integrations')}>
        {t('marketplace.title')}
      </Link>
    </EmptyState>
  );
}

/** A settings page only owners and admins may see (the APIs say the same, with a 403). */
export function AdminOnly({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const { t } = useI18n();
  const { isAdmin, loading } = useCurrentCompany();
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <>
      <SettingsTabs tenantId={tenantId} />
      {isAdmin ? children : <EmptyState icon={<Icon name="lock" size={40} />} title={t('settings.adminsOnly')} />}
    </>
  );
}
